import { type RefObject, useEffect, useState } from 'react'

/**
 * Generic shape for the Heights structure used by the provider.
 * Keep this compatible with your repo types (Type & HeightRecord).
 */
type Heights = Partial<Record<string, Record<string, number>>>

type UseProcessedHeightsOptions = {
  debounceMs?: number
  skipDelayIdsRef?: RefObject<Set<string>>
}

/**
 * Returns a processed heights snapshot that:
 *  - immediately incorporates entries whose ids are present in skipDelayIdsRef
 *  - incorporates the remaining entries after debounceMs (debounced)
 *
 * NOTE: skipDelayIdsRef is read during compareHeights changes — mutations to the Set
 * won't trigger the hook by themselves. This mirrors the behavior in the diff where
 * updates happen when compareHeights changes.
 */
export default function useProcessedHeights(
  compareHeights: Heights,
  { debounceMs = 50, skipDelayIdsRef }: UseProcessedHeightsOptions = {}
): Heights {
  const [processedHeights, setProcessedHeights] = useState<Heights>({})

  const mergeSelective = (prev: Heights, source: Heights, predicate: (id: string) => boolean): Heights => {
    const newHeights: Heights = {}

    // copy previous state so we don't clobber unrelated entries
    Object.entries(prev).forEach(([type, heights]) => {
      if (heights) {
        newHeights[type] = { ...heights }
      }
    })

    // selectively copy from source
    Object.entries(source).forEach(([type, heights]) => {
      if (!heights) {
        return
      }
      Object.entries(heights).forEach(([id, height]) => {
        if (predicate(id)) {
          newHeights[type] ??= {}
          newHeights[type][id] = height
        }
      })
    })

    return newHeights
  }

  // Immediate update for skipDelay ids (runs when compareHeights changes)
  useEffect(() => {
    if (skipDelayIdsRef?.current && skipDelayIdsRef.current.size > 0) {
      setProcessedHeights((prev) =>
        mergeSelective(prev, compareHeights, (id) => Boolean(skipDelayIdsRef.current?.has(id)))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps --  We intentionally only depend on compareHeights here so this effect runs when new measurements arrive (same as original code).
  }, [compareHeights])

  // Debounced update for non-skip ids
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setProcessedHeights((prev) => mergeSelective(prev, compareHeights, (id) => !skipDelayIdsRef?.current?.has(id)))
    }, debounceMs)

    return () => {
      clearTimeout(timeoutId)
    }
    // compareHeights drives the scheduling; debounceMs included to allow configurable timing
  }, [compareHeights, debounceMs, skipDelayIdsRef])

  return processedHeights
}
